import { z } from 'zod';
import { ReleaseStatus, SprintStatus, ReleaseType, CapacityUnit, SprintCommitmentStatus, ReleaseGateStatus, ReleaseReviewerType, ReleaseReviewerStatus, DeploymentEnvironment, DeploymentStatus, ReleaseTicketSource, } from './enums.js';
/**
 * Planning contracts — Sprints & Releases (FR-400 family), expanded for the
 * advanced Sprint & Release Management spec (Parts 1–3).
 *
 * DTO conventions:
 *  - Dates are serialized ISO strings (the API returns Prisma `DateTime` as
 *    JSON strings).
 *  - Fields added during the expansion are `.nullable().optional()` so older
 *    responses (and the legacy thin payloads) still validate.
 *  - `*Input` schemas are what the API accepts on create/update.
 */
const isoDate = z.string(); // ISO datetime string
const nString = z.string().nullable().optional();
const nNumber = z.number().nullable().optional();
const nDate = z.string().nullable().optional();
// ===========================================================================
// Sprint
// ===========================================================================
export const Sprint = z.object({
    id: z.string(),
    name: z.string(),
    code: nString,
    goal: z.string().nullable(),
    description: nString,
    status: SprintStatus,
    // ownership / grouping
    projectId: z.string().nullable(),
    teamId: nString,
    ownerId: nString,
    seriesId: nString,
    linkedReleaseId: nString,
    // dates / duration
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    planningStartDate: nDate,
    durationDays: nNumber,
    // capacity config
    capacityUnit: z.string().optional(),
    defaultHoursPerDay: nNumber,
    focusFactor: nNumber,
    capacityPoints: z.number().nullable(),
    capacityHours: nNumber,
    // rollups
    committedPoints: nNumber,
    completedPoints: nNumber,
    remainingPoints: nNumber,
    committedHours: nNumber,
    completedHours: nNumber,
    remainingHours: nNumber,
    // commitment lock
    commitmentLockedAt: nDate,
    commitmentLockedBy: nString,
    // audit
    createdBy: nString,
    updatedBy: nString,
    createdAt: isoDate,
    updatedAt: z.string().optional(),
    closedAt: nDate,
    deletedAt: nDate,
});
export const CreateSprintInput = z.object({
    name: z.string().min(1),
    code: z.string().optional(),
    goal: z.string().optional(),
    description: z.string().optional(),
    projectId: z.string().optional(),
    teamId: z.string().optional(),
    ownerId: z.string().optional(),
    seriesId: z.string().optional(),
    linkedReleaseId: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    planningStartDate: z.string().optional(),
    durationDays: z.number().int().positive().optional(),
    capacityUnit: CapacityUnit.optional(),
    defaultHoursPerDay: z.number().positive().optional(),
    focusFactor: z.number().min(0).max(1).optional(),
    // Nullable so a capacity can be CLEARED, not only set: `undefined` already
    // means "leave alone" to updateSprint, which left no way to empty the field.
    capacityPoints: nNumber,
    capacityHours: nNumber,
});
export const UpdateSprintInput = CreateSprintInput.partial().extend({
    status: SprintStatus.optional(),
});
// ---- Sprint series (recurring cadence, §8) ----
export const SprintSeries = z.object({
    id: z.string(),
    name: z.string(),
    teamId: z.string().nullable(),
    projectId: z.string().nullable(),
    namingPattern: z.string().nullable(),
    durationDays: z.number(),
    gapDays: z.number(),
    autoCreateNext: z.boolean(),
    defaultTemplateId: z.string().nullable(),
    createdBy: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const CreateSprintSeriesInput = z.object({
    name: z.string().min(1),
    teamId: z.string().optional(),
    projectId: z.string().optional(),
    namingPattern: z.string().optional(),
    durationDays: z.number().int().positive().default(14),
    gapDays: z.number().int().min(0).default(0),
    autoCreateNext: z.boolean().default(false),
    defaultTemplateId: z.string().optional(),
    // how many future sprints to materialize now, and when the first one starts
    count: z.number().int().min(1).max(52).optional(),
    startDate: z.string().optional(),
});
// ---- Sprint bucket (§9) ----
export const SprintBucket = z.object({
    id: z.string(),
    sprintId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    color: z.string().nullable(),
    sortOrder: z.number(),
    capacityPercentage: z.number().nullable(),
    ownerId: z.string().nullable(),
    wipLimit: z.number().nullable(),
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const CreateSprintBucketInput = z.object({
    name: z.string().min(1),
    description: z.string().optional(),
    color: z.string().optional(),
    sortOrder: z.number().int().optional(),
    capacityPercentage: z.number().min(0).max(100).optional(),
    ownerId: z.string().optional(),
    wipLimit: z.number().int().positive().optional(),
});
export const UpdateSprintBucketInput = CreateSprintBucketInput.partial();
// ---- Sprint ticket membership (§14) ----
export const SprintTicket = z.object({
    id: z.string(),
    sprintId: z.string(),
    ticketId: z.string(),
    bucketId: z.string().nullable(),
    commitmentStatus: SprintCommitmentStatus,
    plannedPoints: z.number().nullable(),
    plannedHours: z.number().nullable(),
    scopeAddedAfterStart: z.boolean(),
    scopeRemovedAfterStart: z.boolean(),
    addedAt: nDate,
    addedBy: nString,
    removedAt: nDate,
    removedBy: nString,
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const AddSprintTicketInput = z.object({
    ticketId: z.string().min(1),
    bucketId: z.string().optional(),
    commitmentStatus: SprintCommitmentStatus.optional(),
    plannedPoints: z.number().optional(),
    plannedHours: z.number().optional(),
});
export const UpdateSprintTicketInput = z.object({
    bucketId: z.string().nullable().optional(),
    commitmentStatus: SprintCommitmentStatus.optional(),
    plannedPoints: z.number().nullable().optional(),
    plannedHours: z.number().nullable().optional(),
});
// ---- Sprint capacity (§11) ----
export const SprintCapacity = z.object({
    id: z.string(),
    sprintId: z.string(),
    userId: z.string(),
    role: z.string().nullable(),
    availableDays: z.number().nullable(),
    ptoDays: z.number().nullable(),
    holidayDays: z.number().nullable(),
    dailyHours: z.number().nullable(),
    availabilityPercentage: z.number().nullable(),
    focusFactor: z.number().nullable(),
    capacityHours: z.number().nullable(),
    assignedHours: z.number().nullable(),
    assignedPoints: z.number().nullable(),
    utilizationPercentage: z.number().nullable(),
    status: z.string().optional(), // utilization band (under|healthy|near|over) added by the capacity API
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const UpsertSprintCapacityInput = z.object({
    userId: z.string().min(1),
    role: z.string().optional(),
    availableDays: z.number().optional(),
    ptoDays: z.number().optional(),
    holidayDays: z.number().optional(),
    dailyHours: z.number().optional(),
    availabilityPercentage: z.number().optional(),
    focusFactor: z.number().min(0).max(1).optional(),
});
// ---- Sprint capacity override / PTO (§12) ----
export const SprintCapacityOverride = z.object({
    id: z.string(),
    sprintId: z.string(),
    userId: z.string(),
    date: isoDate,
    availableHours: z.number().nullable(),
    reason: z.string().nullable(),
    createdBy: z.string().nullable(),
    createdAt: isoDate,
});
export const CreateSprintCapacityOverrideInput = z.object({
    userId: z.string().min(1),
    date: z.string().min(1),
    availableHours: z.number().min(0).optional(),
    reason: z.string().optional(),
});
// ---- Sprint retrospective (§22) ----
export const RetroActionItem = z.object({
    text: z.string(),
    ticketId: z.string().optional(),
    done: z.boolean().optional(),
});
export const SprintRetrospective = z.object({
    id: z.string(),
    sprintId: z.string(),
    whatWentWell: z.string().nullable(),
    whatDidNotGoWell: z.string().nullable(),
    actionItems: z.array(RetroActionItem).default([]),
    aiSummary: z.string().nullable(),
    createdBy: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const UpsertSprintRetrospectiveInput = z.object({
    whatWentWell: z.string().optional(),
    whatDidNotGoWell: z.string().optional(),
    actionItems: z.array(RetroActionItem).optional(),
    aiSummary: z.string().optional(),
});
// ===========================================================================
// Release
// ===========================================================================
export const Release = z.object({
    id: z.string(),
    version: z.string(),
    // structured version parts
    major: nNumber,
    minor: nNumber,
    datePart: nString,
    increment: nNumber,
    patchNumber: nNumber,
    type: z.string().optional(),
    // descriptive
    label: z.string().nullable(),
    title: nString,
    description: nString,
    status: ReleaseStatus,
    customerImpact: z.string().nullable(),
    // dates / schedule
    targetDate: z.string().nullable(),
    targetUatDate: nDate,
    uatDeployedAt: nDate,
    uatReviewDeadline: nDate,
    targetProductionDate: nDate,
    productionDeployedAt: nDate,
    releasedAt: nDate,
    // git flow
    sourceBranch: nString,
    uatBranch: nString,
    productionBranch: nString,
    releaseBranch: nString,
    commitSha: nString,
    tagName: nString,
    // legacy json blobs
    notes: z.string().nullable(),
    gates: z.record(z.boolean()).default({}),
    approvals: nString, // legacy json blob (raw string), present on the model
    // ownership / audit
    ownerId: nString,
    createdById: nString,
    updatedById: nString,
    createdAt: isoDate,
    updatedAt: z.string().optional(),
    deletedAt: nDate,
});
export const CreateReleaseInput = z.object({
    // Optional: omitted means "generate the next MAJOR.MINOR.YYMMDD.N".
    version: z.string().min(1).optional(),
    major: z.number().int().optional(),
    minor: z.number().int().optional(),
    datePart: z.string().optional(),
    patchNumber: z.number().int().optional(),
    type: ReleaseType.optional(),
    label: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    customerImpact: z.string().optional(),
    targetDate: z.string().optional(),
    targetUatDate: z.string().optional(),
    targetProductionDate: z.string().optional(),
    sourceBranch: z.string().optional(),
    notes: z.string().optional(),
    ownerId: z.string().optional(),
});
// version + its parsed parts are immutable after create — omit them from updates.
export const UpdateReleaseInput = CreateReleaseInput.omit({ version: true, major: true, minor: true, datePart: true, patchNumber: true }).partial().extend({
    status: ReleaseStatus.optional(),
    uatBranch: z.string().optional(),
    productionBranch: z.string().optional(),
    releaseBranch: z.string().optional(),
    commitSha: z.string().optional(),
    tagName: z.string().optional(),
    uatReviewDeadline: z.string().optional(),
    gates: z.record(z.boolean()).optional(),
});
// ---- Release ticket scope (§30) ----
export const ReleaseTicket = z.object({
    id: z.string(),
    releaseId: z.string(),
    ticketId: z.string(),
    source: z.string().nullable(),
    includedAt: isoDate,
    includedBy: z.string().nullable(),
    releaseNoteRequired: z.boolean(),
    customerImpact: z.boolean(),
    riskLevel: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const AddReleaseTicketInput = z.object({
    ticketId: z.string().min(1),
    source: ReleaseTicketSource.optional(),
    releaseNoteRequired: z.boolean().optional(),
    customerImpact: z.boolean().optional(),
    riskLevel: z.enum(['low', 'medium', 'high']).optional(),
});
/**
 * A per-ticket promotion decision under the opt-out review model. `promoted`
 * exists to reverse a decline, not as a required approval step — an untouched
 * ticket ships when the review window closes.
 */
export const ReleaseTicketDecisionInput = z.object({
    decision: z.enum(['promoted', 'declined']),
    /** Required when declining: tells the team whether to fix it or just wait. */
    reason: z.string().min(1).max(2000).optional(),
});
// ---- Release pull request (§28/§29) ----
export const ReleasePullRequest = z.object({
    id: z.string(),
    releaseId: z.string(),
    ticketId: z.string().nullable(),
    repository: z.string().nullable(),
    prNumber: z.number().nullable(),
    prTitle: z.string().nullable(),
    sourceBranch: z.string().nullable(),
    targetBranch: z.string().nullable(),
    status: z.string().nullable(),
    mergedAt: nDate,
    mergedBy: z.string().nullable(),
    commitSha: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const LinkReleasePullRequestInput = z.object({
    ticketId: z.string().optional(),
    repository: z.string().optional(),
    prNumber: z.number().int().optional(),
    prTitle: z.string().optional(),
    sourceBranch: z.string().optional(),
    targetBranch: z.string().optional(),
    status: z.enum(['open', 'merged', 'closed']).optional(),
    commitSha: z.string().optional(),
});
// ---- Release gate (§32) ----
export const ReleaseGate = z.object({
    id: z.string(),
    releaseId: z.string(),
    gateKey: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    required: z.boolean(),
    status: ReleaseGateStatus,
    ownerId: z.string().nullable(),
    evidenceUrl: z.string().nullable(),
    lastCheckedAt: nDate,
    overrideReason: z.string().nullable(),
    overriddenBy: z.string().nullable(),
    overriddenAt: nDate,
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const UpsertReleaseGateInput = z.object({
    gateKey: z.string().min(1),
    name: z.string().min(1),
    description: z.string().optional(),
    required: z.boolean().optional(),
    status: ReleaseGateStatus.optional(),
    ownerId: z.string().optional(),
    evidenceUrl: z.string().optional(),
});
export const OverrideReleaseGateInput = z.object({
    overrideReason: z.string().min(1),
});
// ---- Release reviewer (§33) ----
export const ReleaseReviewer = z.object({
    id: z.string(),
    releaseId: z.string(),
    userId: z.string(),
    reviewerType: ReleaseReviewerType,
    status: ReleaseReviewerStatus,
    viewedAt: nDate,
    respondedAt: nDate,
    responseComment: z.string().nullable(),
    autoAccepted: z.boolean(),
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const AddReleaseReviewerInput = z.object({
    userId: z.string().min(1),
    reviewerType: ReleaseReviewerType.optional(),
});
export const ReleaseReviewerResponseInput = z.object({
    status: z.enum(['viewed', 'approved', 'rejected', 'changes_requested']),
    responseComment: z.string().optional(),
});
// ---- Release note (§35) ----
export const ReleaseNote = z.object({
    id: z.string(),
    releaseId: z.string(),
    noteType: z.enum(['uat', 'production']),
    contentMarkdown: z.string().nullable(),
    aiGenerated: z.boolean(),
    generatedFrom: z.string().nullable(),
    editedBy: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const UpsertReleaseNoteInput = z.object({
    noteType: z.enum(['uat', 'production']),
    contentMarkdown: z.string().optional(),
    aiGenerated: z.boolean().optional(),
});
/**
 * Emailing the release-note PDF to the team. `userIds` is the recipient list the
 * sender confirmed in the dialog; omitting it means "everyone eligible", and it
 * is filtered by the same rules either way — a client cannot mail an account the
 * server would have skipped.
 */
export const EmailReleaseNotesInput = z.object({
    noteType: z.enum(['uat', 'production']).default('production'),
    subject: z.string().min(1).max(200).optional(),
    message: z.string().max(2000).optional(),
    userIds: z.array(z.string().min(1)).max(200).optional(),
});
// ---- Release deployment (§37) ----
export const ReleaseDeployment = z.object({
    id: z.string(),
    releaseId: z.string(),
    environment: DeploymentEnvironment,
    status: DeploymentStatus,
    branch: z.string().nullable(),
    commitSha: z.string().nullable(),
    deploymentUrl: z.string().nullable(),
    startedAt: nDate,
    completedAt: nDate,
    deployedBy: z.string().nullable(),
    failureReason: z.string().nullable(),
    rollbackReleaseId: z.string().nullable(),
    createdAt: isoDate,
    updatedAt: z.string().optional(),
});
export const CreateReleaseDeploymentInput = z.object({
    environment: DeploymentEnvironment,
    status: DeploymentStatus.optional(),
    branch: z.string().optional(),
    commitSha: z.string().optional(),
    deploymentUrl: z.string().optional(),
    rollbackReleaseId: z.string().optional(),
});
// ---- Release audit log (§37) ----
export const ReleaseAuditLog = z.object({
    id: z.string(),
    releaseId: z.string(),
    entityType: z.string().nullable(),
    entityId: z.string().nullable(),
    action: z.string(),
    before: z.string().nullable(),
    after: z.string().nullable(),
    actorId: z.string().nullable(),
    createdAt: isoDate,
});
// Saved/shared views moved to ./view.ts (promoted to the unified saved_views
// model: visibility, queryText, typed config, per-role/team defaults).
//# sourceMappingURL=planning.js.map